import React, { useEffect } from "react";
import MDButton from "components/MDButton";
import { Backdrop, Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export interface BulkResetRun {
  // Trips finished (succeeded, skipped or failed).
  done: number;
  // Chunked run: the last trip in the request now in flight. Queued run: unused.
  through: number;
  total: number;
  // Set for a queued run: the server does the work, so the browser can close the overlay or the tab.
  queued?: {
    batchId: string;
    running: number;
    // The immediate confirmation, e.g. "12 trips selected for processing, 3 skipped."
    note?: string;
    cancelling?: boolean;
    cancelled?: boolean;
  };
}

interface Props {
  run: BulkResetRun | null;
  // Queued run only: close the overlay, progress continues server-side.
  onBackground?: () => void;
  // Queued run only: skip the trips not started yet.
  onCancel?: () => void;
}

// Full-page block while a bulk reset runs. It covers the filters, the grid, paging and the
// sidebar in one place, so nothing can change the selection or the page under a run in flight,
// and it puts the progress where it cannot be missed. A queued run (the server does the trips)
// can be sent to the background or cancelled from here.
const BulkEventResetOverlay: React.FC<Props> = ({ run, onBackground, onCancel }) => {
  const browserDriven = run !== null && !run.queued;

  // A chunked run lives in this tab: closing or refreshing it stops the run, so the browser asks.
  useEffect(() => {
    if (!browserDriven) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [browserDriven]);

  if (!run) return null;
  const percent = run.total > 0 ? Math.round((run.done / run.total) * 100) : 0;
  const q = run.queued;

  return (
    <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: "rgba(0, 0, 0, 0.55)" }}>
      <Paper elevation={8} sx={{ width: "min(480px, calc(100vw - 32px))", p: 4, textAlign: "center" }}>
        <RestartAltIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {q?.cancelled ? "Cancelling events reset" : "Resetting events"}
        </Typography>
        {q?.note && (
          <Typography variant="body2" sx={{ mb: 1 }} data-testid="bulk-reset-note">
            {q.note}
          </Typography>
        )}
        <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {run.done.toLocaleString()} <Box component="span" sx={{ fontSize: "0.5em", fontWeight: 500 }}>of {run.total.toLocaleString()} trips done</Box>
        </Typography>
        <LinearProgress variant="determinate" value={percent} sx={{ height: 10, borderRadius: 5, my: 2 }} />
        {q ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {q.running > 0 ? `${q.running} running now` : q.cancelled ? "Finishing the trip already running" : "Waiting to start"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This runs on the server. You can keep working: closing this, or the tab, does not stop it. Follow it on the Event
              Resets tab.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              {onBackground && (
                <MDButton variant="contained" color="info" size="small" onClick={onBackground} data-testid="bulk-reset-background">
                  Run in background
                </MDButton>
              )}
              {onCancel && !q.cancelled && (
                <MDButton variant="outlined" color="error" size="small" onClick={onCancel} disabled={q.cancelling} data-testid="bulk-reset-cancel">
                  {q.cancelling ? "Cancelling…" : "Cancel"}
                </MDButton>
              )}
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Working on trips {(run.done + 1).toLocaleString()}–{run.through.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keep this tab open. Closing or refreshing it stops the run; trips already reset stay reset.
            </Typography>
          </>
        )}
      </Paper>
    </Backdrop>
  );
};

export default BulkEventResetOverlay;
